/** TanStack Query hooks for hosted static sites CRUD */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_BASE } from '../lib/api-client'

export interface HostedSite {
  id: string
  url: string
  uploadedBy: string
  filename: string
  siteType: 'html' | 'zip'
  entryPath: string
  totalSizeBytes: number
  fileCount: number
  description: string | null
  summary: string | null
  status: 'processing' | 'ready' | 'failed'
  /** Live version label (e.g. 1.0.0); older API responses may omit it */
  version?: string
  createdAt: number
  updatedAt: number
}

/** One entry of a site's version history (GET /api/sites/:id/versions, newest first) */
export interface SiteVersion {
  id: string
  siteId: string
  version: string
  totalSizeBytes: number
  fileCount: number
  changelog: string | null
  publishedBy: string
  createdAt: number
}

interface ListSitesResponse {
  sites: HostedSite[]
  total: number
}

/** Filter options for listing hosted sites — all fields are optional */
export interface SiteFilters {
  search?: string
  status?: 'ready' | 'processing' | 'failed'
  siteType?: 'html' | 'zip'
  /** Unix ms timestamp — only sites created after this time are returned */
  createdAfter?: number
}

export function useHostedSites(filters?: SiteFilters) {
  const params = new URLSearchParams()
  if (filters?.search) params.set('search', filters.search)
  if (filters?.status) params.set('status', filters.status)
  if (filters?.siteType) params.set('siteType', filters.siteType)
  if (filters?.createdAfter) params.set('createdAfter', String(filters.createdAfter))

  return useQuery<ListSitesResponse>({
    queryKey: ['hosted-sites', filters],
    queryFn: () => apiClient.get<ListSitesResponse>(`/api/sites?${params}`),
  })
}

export function useUploadSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, description, autoSummary }: { file: File; description?: string; autoSummary?: boolean }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (description) formData.append('description', description)
      if (autoSummary) formData.append('autoSummary', 'true')

      const res = await fetch(`${API_BASE}/api/sites`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error((data as { error?: string }).error ?? 'Upload failed')
      }
      return res.json() as Promise<{ id: string; url: string; filename: string; fileCount: number; totalSizeBytes: number }>
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hosted-sites'] }),
  })
}

/** Optimistic description update — rollback on error. Receives full filters for precise cache key. */
export function useUpdateSite(filters?: SiteFilters) {
  const qc = useQueryClient()
  const queryKey = ['hosted-sites', filters] as const
  return useMutation({
    mutationFn: ({ id, description }: { id: string; description: string | null }) =>
      apiClient.patch<{ id: string; description: string | null; updatedAt: number }>(
        `/api/sites/${id}`,
        { description },
      ),
    onMutate: async ({ id, description }) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<ListSitesResponse>(queryKey)
      if (previous) {
        qc.setQueryData<ListSitesResponse>(queryKey, {
          ...previous,
          sites: previous.sites.map((s) =>
            s.id === id ? { ...s, description, updatedAt: Date.now() } : s,
          ),
        })
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['hosted-sites'] }),
  })
}

export function useRegenerateSiteSummary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ status: 'processing' }>(`/api/sites/${id}/summary`, {}),
    onSuccess: () => {
      // Generation runs in the background; refresh shortly to pick up the new summary
      setTimeout(() => qc.invalidateQueries({ queryKey: ['hosted-sites'] }), 3000)
      setTimeout(() => qc.invalidateQueries({ queryKey: ['hosted-sites'] }), 8000)
    },
  })
}

export function useDeleteSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ deleted: boolean }>(`/api/sites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hosted-sites'] }),
  })
}

export function useSiteVersions(siteId: string | null) {
  return useQuery<{ versions: SiteVersion[] }>({
    queryKey: ['hosted-sites', 'versions', siteId],
    queryFn: () => apiClient.get<{ versions: SiteVersion[] }>(`/api/sites/${siteId}/versions`),
    enabled: !!siteId,
  })
}

/** Make an older version live again; refreshes the list (size, file count, version) and the history */
export function useRollbackSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ siteId, versionId }: { siteId: string; versionId: string }) =>
      apiClient.post<{ version: string }>(`/api/sites/${siteId}/rollback`, { versionId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hosted-sites'] }),
  })
}
